import React from 'react';
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronDoubleLeftIcon,
    ChevronDoubleRightIcon,
} from '../../components/icons';
import { cn } from '@/lib/utils';

export interface PaginationProps {
    totalCount: number;
    pageSize: number;
    currentPage: number;
    onPageChange: (page: number) => void;
    selectedCount?: number;
    onPageSizeChange?: (pageSize: number) => void;
    pageSizeOptions?: number[];
    className?: string;
}

/** Tokens explícitos — evita colisão Tailwind `text-secondary` → `--color-secondary` (surface-2). */
const NAV_BTN = cn(
    'inline-flex size-11 shrink-0 items-center justify-center rounded-lg border',
    'touch-manipulation transition-[background-color,color,border-color] duration-150 ease-out',
    'enabled:cursor-pointer enabled:border-subtle enabled:bg-card enabled:text-[var(--text-secondary)]',
    'enabled:hover:bg-surface-1 enabled:hover:text-brand-600 enabled:hover:border-brand-200',
    'enabled:focus-visible:outline-none enabled:focus-visible:ring-4 enabled:focus-visible:ring-primary/20',
    'enabled:active:bg-primary-subtle enabled:active:text-primary enabled:active:border-brand-200',
    'disabled:cursor-not-allowed disabled:border-subtle disabled:bg-surface-1 disabled:text-[var(--text-muted)]',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
    'md:size-8 md:rounded-md',
);

const PAGE_TEXT = 'font-sans text-sm font-normal text-[var(--text-secondary)]';

const SELECT_CLASS = cn(
    'h-11 min-w-14 rounded-lg border border-subtle bg-card px-s2',
    'font-mono text-sm font-normal tabular-nums text-default',
    'transition-[border-color,box-shadow] duration-150 ease-out',
    'hover:border-brand-200',
    'focus-visible:border-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/20',
    'touch-manipulation md:h-8 md:rounded-md',
);

const ICON = 'size-4 shrink-0 text-current';

function PageNavButton({
    onClick,
    enabled,
    labels,
    title,
    icon,
    className,
}: {
    onClick: () => void;
    enabled: boolean;
    labels: readonly [string, string];
    title: string;
    icon: React.ReactNode;
    className?: string;
}) {
    return (
        <button
            type="button"
            className={cn(NAV_BTN, className)}
            onClick={onClick}
            disabled={!enabled}
            aria-label={enabled ? labels[0] : labels[1]}
            title={enabled ? title : undefined}
        >
            {icon}
        </button>
    );
}

export default function Pagination({
    totalCount,
    pageSize,
    currentPage,
    onPageChange,
    selectedCount = 0,
    onPageSizeChange,
    pageSizeOptions = [10, 20, 30, 40, 50],
    className = '',
}: PaginationProps) {
    const totalPages = Math.max(Math.ceil(totalCount / pageSize), 1);
    const canPrevious = currentPage > 1;
    const canNext = currentPage < totalPages;

    return (
        <nav
            aria-label="Paginação"
            className={cn('flex flex-col gap-s3 px-s4 py-s3 md:flex-row md:items-center md:justify-between md:gap-s3 md:px-s6', PAGE_TEXT, className)}
        >
            <p className={cn('w-full text-center md:w-auto md:text-left', selectedCount === 0 && 'max-md:hidden')}>
                {selectedCount} de {totalCount} linha(s) selecionada(s).
            </p>

            <div className="flex w-full items-center justify-between gap-s2 md:w-auto md:justify-end md:gap-4">
                <div className="flex min-w-0 items-center gap-s2" role="group" aria-label="Navegação de páginas">
                    <PageNavButton
                        onClick={() => onPageChange(1)}
                        enabled={canPrevious}
                        labels={['Ir para a primeira página', 'Primeira página indisponível']}
                        title="Primeira página"
                        icon={<ChevronDoubleLeftIcon className={ICON} aria-hidden />}
                        className="hidden sm:inline-flex"
                    />
                    <PageNavButton
                        onClick={() => onPageChange(currentPage - 1)}
                        enabled={canPrevious}
                        labels={['Ir para a página anterior', 'Página anterior indisponível']}
                        title="Página anterior"
                        icon={<ChevronLeftIcon className={ICON} aria-hidden />}
                    />
                    <p className={cn('m-0 whitespace-nowrap', PAGE_TEXT)} aria-live="polite">
                        <span className="md:hidden">
                            <span className="font-mono tabular-nums text-default">{currentPage}</span>
                            <span aria-hidden>/</span>
                            <span className="font-mono tabular-nums">{totalPages}</span>
                        </span>
                        <span className="hidden md:inline">
                            Página <span className="font-mono tabular-nums text-default">{currentPage}</span> de{' '}
                            <span className="font-mono tabular-nums">{totalPages}</span>
                        </span>
                    </p>
                    <PageNavButton
                        onClick={() => onPageChange(currentPage + 1)}
                        enabled={canNext}
                        labels={['Ir para a próxima página', 'Próxima página indisponível']}
                        title="Próxima página"
                        icon={<ChevronRightIcon className={ICON} aria-hidden />}
                    />
                    <PageNavButton
                        onClick={() => onPageChange(totalPages)}
                        enabled={canNext}
                        labels={['Ir para a última página', 'Última página indisponível']}
                        title="Última página"
                        icon={<ChevronDoubleRightIcon className={ICON} aria-hidden />}
                        className="hidden sm:inline-flex"
                    />
                </div>

                {onPageSizeChange && (
                    <label className={cn('flex shrink-0 items-center gap-s2', PAGE_TEXT)} htmlFor="pagination-page-size">
                        <span className="hidden sm:inline">Linhas por página</span>
                        <select
                            id="pagination-page-size"
                            name="pagination-page-size"
                            value={pageSize}
                            onChange={(e) => onPageSizeChange(Number(e.target.value))}
                            aria-label="Linhas por página"
                            className={SELECT_CLASS}
                        >
                            {pageSizeOptions.map((size) => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
            </div>
        </nav>
    );
}
